// src/components/MassiveTaskDialog.tsx
import React, { memo, useEffect, useState, useMemo } from "react";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { Select } from "./Select"; // Asumiendo que tienes este componente
import { InputWithCornerHint } from "../atoms/InputWithCornerHint"; // Asumiendo que tienes este componente
import { MultiSelect } from "./MultiSelect"; // Asumiendo que tienes este componente
import { t } from "i18next";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/auth/auth.slice";
import { User as AuthUserType } from "@/app/auth/auth.type";
import { useParams } from "react-router-dom";
import { useFetchBranchsMutation } from "@/app/branchs/branchs.api";
import { useGymsByAccessUserQuery } from "@/app/gyms/gyms.api";
import {
  useGetSupportStaffQuery,
  useMassLeadTasksMutation,
} from "@/pages/CatchmentPage/api/catchmeApi.api"; // Ajusta la ruta si es necesario
import { useTagsQuery } from "@/pages/TasksPage/api/taskApi";
import { optionsGender as defaultOptionsGender } from "@/shared/constants/constants";
import { toasts } from "@/shared/utils/toasts";

// Tipos
type Option = { id: number | string; name: string };

interface FormValues {
  branch_filter: number | null;
  sex_filter: string | null;
  stage: string | null;
  has_pending_tasks: string | null;
  max_leads: string;
  name_task: string;
  date_end: string;
  support: number | null;
  labels_id: number[];
  execution_day: string;
}

interface MassiveTaskDialogProps {
  setOpen: (open: boolean) => void;
  open: boolean;
  branch_id_context?: number;
}

// Opciones estáticas
const businessStagesOptions: Option[] = [
  { id: "interested", name: t("recovery.views.interested", "Interesado") },
  {
    id: "initial_contact",
    name: t("recovery.views.initial_contact", "Contacto Inicial"),
  },
  {
    id: "presentation",
    name: t("recovery.views.presentation", "Presentación"),
  },
  { id: "negotiation", name: t("recovery.views.negotiation", "Negociación") },
  { id: "won", name: t("recovery.views.won", "Ganado") },
  { id: "lost", name: t("recovery.views.lost", "Perdido") },
  { id: "quarantine", name: t("recovery.views.quarantine", "Cuarentena") },
];

const optionsHasPendingTasks: Option[] = [
  { id: "0", name: t("lead.views.all", "Todos") },
  { id: "1", name: t("lead.views.yes", "Sí") },
  { id: "2", name: t("lead.views.no", "No") },
];

const optionsGender: Option[] = defaultOptionsGender.map((g) => ({
  id: g.name,
  name: g.name,
}));

const MassiveTaskDialog: React.FC<MassiveTaskDialogProps> = ({
  setOpen,
  open,
  branch_id_context,
}) => {
  const { id: routeBranchIdParam } = useParams<{ id: string }>();
  const user: AuthUserType | null = useSelector(selectUser);

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const branchIdForQueries = routeBranchIdParam
    ? parseInt(routeBranchIdParam)
    : (branch_id_context ?? user?.branch?.id ?? undefined);

  const getInitialFormValues = (): FormValues => ({
    branch_filter: branchIdForQueries ?? null,
    sex_filter: null,
    stage: null,
    has_pending_tasks: "0", // Default to "All"
    max_leads: "",
    name_task: "",
    date_end: "",
    support: null,
    labels_id: [],
    execution_day: "",
  });
  const [formValues, setFormValues] = useState<FormValues>(
    getInitialFormValues()
  );

  // --- RTK Query Hooks ---
  const {
    data: supportStaffData,
    isLoading: isLoadingSupportStaff,
    refetch: refetchSupportStaff,
  } = useGetSupportStaffQuery(
    { branch_id: branchIdForQueries?.toString() ?? "" },
    { skip: !open || !branchIdForQueries }
  );
  const {
    data: tagsData,
    isLoading: isLoadingTags,
    refetch: refetchTags,
  } = useTagsQuery(
    { branch_id: branchIdForQueries?.toString() ?? "" },
    { skip: !open || !branchIdForQueries }
  );
  const [massLeadTasks, { isLoading: isLoadingMassTaskMutation }] =
    useMassLeadTasksMutation();

  // Carga de Gimnasios y Sucursales
  const [gymId, setGymId] = useState<number | undefined>(user?.branch?.gymId);
  const { data: gymsByAccessUserData, isLoading: isLoadingGyms } =
    useGymsByAccessUserQuery({}, { skip: !!gymId || !open });
  const [fetchBranchs, { isLoading: isLoadingBranches }] =
    useFetchBranchsMutation();
  const [optionsBranches, setOptionsBranches] = useState<Option[]>([]);

  // Efecto para resetear/actualizar el formulario cuando se abre/cierra el modal o cambia el contexto
  useEffect(() => {
    if (!open) {
      setFormValues(getInitialFormValues()); // Reset al cerrar
      setStatusMessage(null);
    } else {
      // Cuando se abre, o si branchIdForQueries cambia mientras está abierto,
      // resetea el formulario con el nuevo branch_id_context.
      setFormValues(getInitialFormValues());
      // Si branchIdForQueries es válido, dispara la recarga de datos dependientes
      if (branchIdForQueries) {
        refetchSupportStaff();
        refetchTags();
      }
    }
  }, [open, branchIdForQueries]); // Depender de branchIdForQueries para actualizar si cambia el contexto

  useEffect(() => {
    if (
      open &&
      !gymId &&
      gymsByAccessUserData &&
      gymsByAccessUserData.length > 0
    ) {
      setGymId(gymsByAccessUserData[0].id);
    } else if (open && user?.branch?.gymId && !gymId) {
      // Si el modal se abre y ya tenemos gymId del user
      setGymId(user.branch.gymId);
    }
  }, [open, gymId, gymsByAccessUserData, user]);

  useEffect(() => {
    const fetchBranchesData = async () => {
      if (!gymId || !open) {
        if (open) setOptionsBranches([]); // Limpiar si no hay gymId pero el modal está abierto
        return;
      }
      try {
        const responseBranchs = await fetchBranchs({
          limit: 200,
          gymId,
        }).unwrap();
        const mappedBranches = responseBranchs.data.map((b: any) => ({
          id: b.id,
          name: b.branch,
        }));
        setOptionsBranches(mappedBranches);
      } catch (err) {
        console.error("Error fetching branches:", err);
        setOptionsBranches([]);
      }
    };
    fetchBranchesData();
  }, [gymId, fetchBranchs, open]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]:
        type === "number" && value === ""
          ? ""
          : type === "number"
            ? Number(value)
            : value,
    }));
  };

  const handleSelectChange = (
    name: keyof FormValues,
    selectedOption: Option | null
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.id : null,
    }));
  };

  const handleMultiSelectChange = (
    name: "labels_id",
    selectedOptions: Option[]
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: selectedOptions.map((opt) => opt.id as number),
    }));
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    toasts.success("Tareas created correctamente");
    setOpen(false);

    if (e) e.preventDefault();
    setStatusMessage(null);
    setIsSubmittingForm(true);

    if (
      !formValues.name_task ||
      !formValues.date_end ||
      formValues.support === null
    ) {
      setStatusMessage(
        t(
          "errors.fill_required_fields_task",
          "Por favor, completa: nombre de tarea, fecha de vencimiento y a quién asignar."
        )
      );
      setIsSubmittingForm(false);
      return;
    }

    // --- CORRECCIÓN DEL PAYLOAD ---
    // Se ajustan los nombres de los campos para que coincidan con el nuevo controlador 'storeMassive'
    const payload = {
      filters: {
        branch_id: formValues.branch_filter ?? undefined,
        // CORRECCIÓN LÓGICA: Manejar correctamente todos los casos de sexo
        sex: formValues.sex_filter
          ? formValues.sex_filter === "Masculino"
            ? "Hombre"
            : "Mujer"
          : undefined,
        stage: formValues.stage || undefined,
        has_pending_tasks: formValues.has_pending_tasks || undefined,
        max_users: formValues.max_leads
          ? parseInt(formValues.max_leads)
          : undefined,
      },
      task_details: {
        // AJUSTE DE NOMBRES DE CAMPOS
        task: formValues.name_task, // 'name_task' ahora es 'task'
        date: formValues.date_end, // 'date_task' ahora es 'date'
        assigned_id: formValues.support, // 'support' ahora es 'assigned_id'
        labels_id: formValues.labels_id, // Este ya estaba bien
        // 'execution_day' no está en el controlador, se omite o se añade si es necesario
      },
    };

    // Este bloque para eliminar 'undefined' ya funciona bien
    Object.keys(payload.filters).forEach((keyStr) => {
      const key = keyStr as keyof typeof payload.filters;
      if (payload.filters[key] === undefined) {
        delete payload.filters[key];
      }
    });

    console.log(
      "Enviando payload EXACTO a la API:",
      JSON.stringify(payload, null, 2)
    );

    try {
      // Asegúrate que useMassLeadTasksMutation apunta a la nueva ruta
      const response = await massLeadTasks(payload).unwrap();
      setStatusMessage(
        response.message ||
          t(
            "success.tasks_created_successfully",
            "Tareas creadas exitosamente."
          )
      );
      setFormValues(getInitialFormValues());
    } catch (err: any) {
      console.error("Error en massLeadTasks:", err);
      let errorMessage = t(
        "errors.general_error",
        "Ocurrió un error inesperado."
      );
      if (err.data) {
        errorMessage = err.data.message || errorMessage;
        if (err.data.errors && typeof err.data.errors === "object") {
          const errorDetails = Object.entries(err.data.errors)
            .map(
              ([field, messages]) =>
                `${t(`fields.${field}`, field)}: ${(messages as string[]).join(", ")}`
            )
            .join("; ");
          errorMessage += ` ${t("errors.details", "Detalles")}: ${errorDetails}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setStatusMessage(`${t("errors.error_prefix", "Error")}: ${errorMessage}`);
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const mappedSupportStaffOptions: Option[] = useMemo(
    () => supportStaffData?.map((s) => ({ id: s.id, name: s.name })) || [],
    [supportStaffData]
  );

  const mappedTagsOptions: Option[] = useMemo(
    () =>
      tagsData?.map((t: any) => ({ id: t.id, name: t.label || t.name })) || [],
    [tagsData]
  );

  return (
    <DialogCreate
      OnclickCreate={handleSubmit}
      OnclickCancel={() => {
        setOpen(false); /* El reset ya se hace en useEffect[open] */
      }}
      tittleButtonCreate={t("general.create", "Crear")}
      tittleButtonCancel={t("general.cancel", "Cancelar")}
      open={open}
      setOpen={setOpen}
      tittle={t("lead.views.massive_tasks", "Tareas masivas")}
      subTittle={t(
        "massive_task.creation_subtitle",
        "Creación de tareas masivas"
      )}
      isLoading={
        isSubmittingForm ||
        isLoadingMassTaskMutation ||
        isLoadingSupportStaff ||
        isLoadingTags ||
        isLoadingBranches ||
        isLoadingGyms
      } // Estado de carga más completo
    >
      <form onSubmit={handleSubmit}>
        <div className="mt-6 mx-2">
          {statusMessage && (
            <div
              className={`p-4 mb-4 text-sm rounded-lg ${
                statusMessage.startsWith(t("errors.error_prefix", "Error"))
                  ? "bg-red-100 text-red-700"
                  : "bg-green-100 text-green-700"
              }`}
              role="alert"
            >
              {statusMessage}
            </div>
          )}
          <div className="space-y-12">
            {/* Sección de Filtros */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
              <div className="flex items-center justify-center h-full text-center">
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("filters.title", "Filtros")}
                </h2>
              </div>
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                <div className="col-span-full">
                  <Select
                    selected={optionsBranches.find(
                      (opt) => opt.id === formValues.branch_filter
                    )}
                    setSelected={(option) =>
                      handleSelectChange("branch_filter", option)
                    }
                    options={optionsBranches}
                    tittle={t("user.views.branch")}
                    name="branch_filter"
                    isLoading={isLoadingBranches || isLoadingGyms}
                  />
                </div>
                <div className="col-span-full">
                  <Select
                    options={optionsGender}
                    selected={optionsGender.find(
                      (opt) => opt.id === formValues.sex_filter
                    )}
                    setSelected={(option) =>
                      handleSelectChange("sex_filter", option)
                    }
                    tittle={t("user.views.sex")}
                    name="sex_filter"
                  />
                </div>
                <div className="col-span-full">
                  <Select
                    selected={businessStagesOptions.find(
                      (opt) => opt.id === formValues.stage
                    )}
                    setSelected={(option) =>
                      handleSelectChange("stage", option)
                    }
                    options={businessStagesOptions}
                    tittle={t("recovery.views.business_stages")}
                    name="stage"
                  />
                </div>
                <div className="col-span-full">
                  <Select
                    selected={optionsHasPendingTasks.find(
                      (opt) => opt.id === formValues.has_pending_tasks
                    )}
                    setSelected={(option) =>
                      handleSelectChange("has_pending_tasks", option)
                    }
                    options={optionsHasPendingTasks}
                    tittle={t("lead.views.lead_no_tasks")}
                    name="has_pending_tasks"
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("lead.views.maximum_leads")}
                    id="max_leads"
                    name="max_leads"
                    type="number"
                    placeholder={t("lead.views.maximum_leads")}
                    onChange={handleChange}
                    value={formValues.max_leads}
                  />
                </div>
              </div>
            </div>

            {/* Sección de Tarea */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
              <div className="flex items-center justify-center h-full text-center">
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("task.title", "Tarea")}
                </h2>
              </div>
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("lead.views.task")}
                    span={t("general.mandatory", "*")}
                    id="name_task"
                    name="name_task"
                    type="text"
                    placeholder={t("lead.views.task")}
                    value={formValues.name_task}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("task.due_date", "Fecha de vencimiento")}
                    id="date_end"
                    name="date_end"
                    type="date"
                    value={formValues.date_end}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t(
                      "task.execution_day",
                      "Fecha de ejecución (opcional)"
                    )}
                    id="execution_day"
                    name="execution_day"
                    type="date"
                    value={formValues.execution_day}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-span-full">
                  <Select
                    options={mappedSupportStaffOptions}
                    selected={mappedSupportStaffOptions.find(
                      (opt) => opt.id === formValues.support
                    )}
                    setSelected={(option) =>
                      handleSelectChange("support", option)
                    }
                    tittle={t("task.assign_to", "Asignar a")}
                    name="support"
                    isLoading={isLoadingSupportStaff}
                  />
                </div>
                <div className="col-span-full">
                  <MultiSelect
                    options={mappedTagsOptions}
                    selectedOptions={mappedTagsOptions.filter((tag) =>
                      formValues.labels_id.includes(tag.id as number)
                    )}
                    setSelectedOptions={(selectedOpts: Option[]) =>
                      handleMultiSelectChange("labels_id", selectedOpts)
                    }
                    title={t("task.views.labels")}
                    isLoading={isLoadingTags}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </DialogCreate>
  );
};

export default memo(MassiveTaskDialog);
